import { DocumentPreviewSkeleton } from "@/components/DocumentPreviewSkeleton";
import { FILE_TYPE, type FileType } from "@/constants/storage";
import { getFileType } from "@/utils/storageUtils";
import { type LoadError, type PageChangeEvent } from "@react-pdf-viewer/core";
import { renderAsync } from "docx-preview";
import { X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { FILE_VIEW_MODE, type FileViewMode } from "../constants/fileViewMode";
import { useCaseFileOcrText } from "../hooks/useCaseFileOcrText";
import "../styles/FileSidePanel.css";
import PdfViewerWithZoom from "./PDFViewer/PdfViewerWithZoom";
import { OcrTextBody, SpreadsheetViewer, TextViewer } from "./fileSidePanel/index";

interface FileSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  fileName?: string;
  fileId?: string;
  fileUrl: string;
  initialPage?: number;
  initialWidth?: number;
  topOffset?: number;
  rightOffset?: number | string;
  minWidth?: number;
  maxWidth?: number;
  onWidthChange?: (width: number) => void;
  /** Called with a 1-based page number. */
  onPageChange?: (page: number) => void;
  onPdfLoadError?: (error: LoadError) => void;
  onPdfLoadSuccess?: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

interface NonPdfViewerSource {
  fileUrl: string;
  fileName: string;
  wordContainerRef: React.RefObject<HTMLDivElement | null>;
}

// Only called for the non-PDF, non-OCR-mode case — PDF and OCR mode are handled separately above
// since they aren't "just another file type" (PDF manages its own OCR toggle internally).
function renderNonPdfViewer(
  fileType: FileType,
  { fileUrl, fileName, wordContainerRef }: NonPdfViewerSource
) {
  switch (fileType) {
    case FILE_TYPE.WORD:
      return (
        <div
          key="word-viewer"
          ref={wordContainerRef}
          className="word-preview-container h-full w-full overflow-auto"
        />
      );
    case FILE_TYPE.SPREADSHEET:
      return <SpreadsheetViewer key="spreadsheet-viewer" fileUrl={fileUrl} fileName={fileName} />;
    case FILE_TYPE.IMAGE:
      return (
        <div
          key="image-viewer"
          className="flex h-full w-full items-start justify-center overflow-auto p-4"
        >
          <img src={fileUrl} alt={fileName} className="block h-auto max-w-full" />
        </div>
      );
    case FILE_TYPE.TEXT:
      return <TextViewer key="text-viewer" fileUrl={fileUrl} fileName={fileName} />;
    default:
      return null;
  }
}

const FileSidePanel = ({
  isOpen,
  onClose,
  fileName,
  fileId,
  fileUrl,
  initialPage = 0,
  initialWidth = 500,
  topOffset = 64,
  rightOffset = 0,
  minWidth = 300,
  maxWidth = 800,
  onWidthChange,
  onPageChange,
  onPdfLoadError,
  onPdfLoadSuccess,
  isFullscreen = false,
  onToggleFullscreen,
}: FileSidePanelProps) => {
  const [panelWidth, setPanelWidth] = useState(initialWidth);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(0);
  const [viewMode, setViewMode] = useState<FileViewMode>(FILE_VIEW_MODE.ORIGINAL);
  const wordContainerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const widthRef = useRef<number>(initialWidth);

  const fileType = getFileType(fileName);
  const isWordFile = fileType === FILE_TYPE.WORD;
  const isPdfFile = fileType === FILE_TYPE.PDF;

  const ocrStatus = useCaseFileOcrText(fileId, viewMode === FILE_VIEW_MODE.OCR);
  const ocrVisiblePageRef = useRef<number>(currentPage);

  const handleOcrPageChange = useCallback((page: number) => {
    ocrVisiblePageRef.current = page;
    setCurrentPage(page);
  }, []);

  const handlePdfPageChange = useCallback(
    (e: PageChangeEvent) => {
      setCurrentPage(e.currentPage);
      onPageChange?.(e.currentPage + 1);
    },
    [onPageChange]
  );

  const handleViewModeChange = useCallback(
    (newMode: FileViewMode) => {
      if (viewMode === FILE_VIEW_MODE.OCR && newMode === FILE_VIEW_MODE.ORIGINAL) {
        setCurrentPage(ocrVisiblePageRef.current);
      }
      if (newMode === FILE_VIEW_MODE.OCR) {
        ocrVisiblePageRef.current = currentPage;
      }
      setViewMode(newMode);
    },
    [viewMode, currentPage]
  );

  // Keyed on fileId (not fileUrl) so a signed-URL refresh doesn't reset view mode / page.
  useEffect(() => {
    setViewMode(FILE_VIEW_MODE.ORIGINAL);
  }, [fileId]);

  useEffect(() => {
    setCurrentPage(initialPage);
  }, [fileId, initialPage]);

  const handleResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const handle = e.currentTarget;
      const startX = e.clientX;
      const startWidth = widthRef.current;

      handle.setPointerCapture(e.pointerId);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";

      const onMove = (moveEvent: PointerEvent) => {
        const delta = startX - moveEvent.clientX;
        const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + delta));
        widthRef.current = newWidth;
        if (panelRef.current) {
          panelRef.current.style.width = `${newWidth}px`;
        }
        document.documentElement.style.setProperty("--panel-width", `${newWidth}px`);
      };

      // Single teardown for every drag-end path. Bound to pointerup *and*
      // pointercancel so an interrupted gesture (touch/pen interruption, lost
      // capture, browser taking over) can't leave the move listener attached or
      // the global body styles stuck.
      const endResize = () => {
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", endResize);
        handle.removeEventListener("pointercancel", endResize);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        setPanelWidth(widthRef.current);
        onWidthChange?.(widthRef.current);
      };

      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", endResize);
      handle.addEventListener("pointercancel", endResize);
    },
    [minWidth, maxWidth, onWidthChange]
  );

  // Safety net: if the panel unmounts mid-drag the handle's listeners vanish
  // with it, but the global body styles set in handleResizeStart would linger.
  // Clear them on unmount so the page never gets stuck non-selectable.
  useEffect(
    () => () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    },
    []
  );

  useEffect(() => {
    const loadWordDocument = async () => {
      if (
        viewMode !== FILE_VIEW_MODE.ORIGINAL ||
        !isWordFile ||
        !wordContainerRef.current ||
        !fileUrl
      ) {
        return;
      }
      try {
        const response = await fetch(fileUrl);
        const blob = await response.blob();

        if (wordContainerRef.current) {
          wordContainerRef.current.innerHTML = "";
        }

        await renderAsync(blob, wordContainerRef.current, undefined, {
          className: "docx-preview",
          inWrapper: false,
          ignoreWidth: true,
          ignoreHeight: true,
          ignoreFonts: false,
          breakPages: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
        });

        const pages = wordContainerRef.current.querySelectorAll(".docx > section");
        setTotalPages(pages.length);

        const container = wordContainerRef.current;
        const handleScroll = () => {
          let maxVisiblePage = 0;
          let maxVisibleArea = 0;

          pages.forEach((page, index) => {
            const rect = page.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const visibleHeight =
              Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top);

            if (visibleHeight > maxVisibleArea) {
              maxVisibleArea = visibleHeight;
              maxVisiblePage = index;
            }
          });

          setCurrentPage(maxVisiblePage);
        };

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
      } catch (error) {
        console.error("Error rendering Word document:", error);
      }
    };

    loadWordDocument();
  }, [fileUrl, isWordFile, viewMode]);

  if (!isOpen) return null;

  const containerStyle: React.CSSProperties = {
    position: "fixed",
    right: rightOffset,
    top: `${topOffset}px`,
    height: `calc(100vh - ${topOffset}px)`,
    background: "white",
    boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.1)",
    display: "flex",
    flexDirection: "column",
    zIndex: "var(--z-drawer)",
    width: `${panelWidth}px`,
    overflow: "visible",
  };

  const ocrPanel = (
    <OcrTextBody
      status={ocrStatus}
      currentPage={currentPage}
      onOcrPageChange={handleOcrPageChange}
    />
  );
  const showViewModeToggle = Boolean(fileId);

  const viewModeSelect = showViewModeToggle ? (
    <select
      className="file-side-panel-view-mode-select"
      value={viewMode}
      onChange={(e) => handleViewModeChange(e.target.value as FileViewMode)}
      aria-label="View mode"
    >
      <option value={FILE_VIEW_MODE.ORIGINAL}>Original</option>
      <option value={FILE_VIEW_MODE.OCR}>OCR</option>
    </select>
  ) : null;

  return (
    <div
      ref={panelRef}
      className={isFullscreen ? "file-side-panel-fullscreen" : undefined}
      style={isFullscreen ? undefined : containerStyle}
    >
      {!isFullscreen && <div className="resize-handle-left" onPointerDown={handleResizeStart} />}
      <div className="file-side-panel">
        {!isPdfFile && (
          <div className="file-side-panel-header">
            <h3>{fileName}</h3>
            <div className="header-right">
              {totalPages > 0 && isWordFile && viewMode === FILE_VIEW_MODE.ORIGINAL && (
                <div className="page-counter">
                  Page {currentPage + 1}/{totalPages}
                </div>
              )}
              {viewModeSelect}
              <button type="button" onClick={onClose}>
                <X size={20} />
              </button>
            </div>
          </div>
        )}
        <div className="file-side-panel-content">
          {!fileUrl ? (
            <DocumentPreviewSkeleton variant="full" />
          ) : isPdfFile ? (
            <PdfViewerWithZoom
              fileUrl={fileUrl}
              initialPage={currentPage}
              onPageChange={handlePdfPageChange}
              onDocumentLoad={(e) => {
                setTotalPages(e.doc.numPages);
                onPdfLoadSuccess?.();
              }}
              fileName={fileName}
              currentPage={currentPage}
              totalPages={totalPages}
              onClose={onClose}
              showViewModeToggle={showViewModeToggle}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              ocrContent={ocrPanel}
              onLoadError={onPdfLoadError}
              isFullscreen={isFullscreen}
              onToggleFullscreen={onToggleFullscreen}
            />
          ) : viewMode === FILE_VIEW_MODE.OCR ? (
            <div className="file-side-panel-ocr">{ocrPanel}</div>
          ) : (
            // A non-PDF file type is only ever derived from a defined fileName.
            renderNonPdfViewer(fileType, { fileUrl, fileName: fileName ?? "", wordContainerRef })
          )}
        </div>
      </div>
    </div>
  );
};

export default FileSidePanel;
